import { createSelector, createFeatureSelector } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { CropAid } from 'src/app/crop-aid/crop-aid.model';
import { selectCropAids } from 'src/app/crop-aid/store/crop-aid.selectors';
import {
  TemplateState,
  Treatment,
  TreatmentSchema,
  AidAdvice,
  Template,
} from 'src/app/shared/models';

export const selectTemplate = createFeatureSelector<TemplateState>('template');

export const selectEditingTemplate = createSelector(
  selectTemplate,
  (store: TemplateState) => store.editingTemplate,
);

export const selectEditingTreatments = createSelector(selectEditingTemplate, (template) =>
  template && template.treatments ? template.treatments : [],
);

export const selectTreatmentNamesAndDosages = createSelector(
  selectCropAids,
  (cropAids: CropAid[], treatment: Treatment): string =>
    treatment.aidAdvices
      .map((aidAdvice) => {
        const cropAidData = cropAids.filter((cropAid) => cropAid.id === aidAdvice.cropAidId);
        return cropAidData.length
          ? `${cropAidData[0]?.name} ${aidAdvice.dosage} ${cropAidData[0].unitOfMeasurement}`
          : '';
      })
      .join(', '),
);

export const selectEditingTreatmentSchemas = createSelector(selectEditingTemplate, (template) => {
  if (!template || !template.treatmentSchemas) {
    return [];
  }
  const schemas: TreatmentSchema[] = cloneDeep(template.treatmentSchemas) as TreatmentSchema[];
  return schemas.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
});

export const areAidAdvicesValid = createSelector(
  selectCropAids,
  (cropAids: CropAid[], aidAdvices: AidAdvice[]) => {
    if (!cropAids) {
      return true;
    }

    return aidAdvices.every((aidAdvice) => {
      const cropAidFound = cropAids.find((cropAid) => cropAid.id === aidAdvice.cropAidId);

      return cropAidFound ? cropAidFound.isActive : false;
    });
  },
);

export const areTreatmentsValid = createSelector(selectEditingTemplate, (template: Template) =>
  template?.treatments.every(
    (treatment) =>
      !treatment.isCorrupt && (!template.isPlanetProof || !treatment.forbiddenForPlanetProof),
  ),
);
